import { Card } from '@material-ui/core'
import { remove as removeDiacritics } from 'diacritics'
import React, { useEffect, useState } from 'react'
import { useIntl } from 'react-intl'
import { Meteor } from 'meteor/meteor'
import Highlighter from 'react-highlight-words'
import { useHistory } from 'react-router-dom'

import TextField from '@material-ui/core/TextField'
import Search from '@material-ui/icons/Search'
import makeStyles from '@material-ui/styles/makeStyles/makeStyles'
import Autocomplete from '@material-ui/lab/Autocomplete'

import EVENTS from '../../constants/events'
import { resetFilters } from '../../constants/filters'
import useCategories from '../../hooks/useCategories'
import useMobile from '../../hooks/useIsMobile'
import trackEvent from '../../lib/trackEvent'
import { RESET_SEARCH } from '../../reducers/searchLawyer'

import { dispatch } from '../../store'
import ChipCategory from '../ChipCategory'
import ChipCategoryLawyer from '../ChipCategoryLawyer'
import LawyerDomain from '../LawyerDomain'

type Props = {
  classesPaper: Object,
  newCategories: Array<Object>,
  theme: Object,
  profile: Object,
  setValueSearch: Function,
  valueInit: any,
  navbar?: boolean,
}

const AutocompleteCategorie = ({ setValueSearch, valueInit, navbar = false }: Props) => {
  const [input, setInput] = useState('')
  const [loading, setLoading] = useState(false)
  const [lawyers, setLawyers] = useState([])
  const { categories } = useCategories()

  const classesTextField = useStylesTextField()
  const classes = useStyle()
  const { formatMessage } = useIntl()
  const history = useHistory()
  const isMobile = useMobile()

  useEffect(() => {
    fetchData(input)
  }, [input])

  const handleChange = (event, value: string): void => {
    setValueSearch(value)
  }

  const handlekeyPress = ({ target: { value } }) => {
    setInput(value)
  }

  const fetchData = async (_text) => {
    if (_text) {
      setLoading(true)

      const results = await Meteor.promise('searchLawyer', { name: _text })

      setLoading(false)
      setLawyers(results)
    }
  }

  const handleClickOnLawyer = (event, item) => {
    event.preventDefault()
    event.stopPropagation()

    dispatch({ type: RESET_SEARCH })

    resetFilters()

    trackEvent(EVENTS.SEARCH.VIEW_PROFILE_FROM_SEARCHBAR, { lawyer: item })

    history.push(`/lawyer/${item._id}`)
  }

  const haveKeywordMatch = (keywords = [], regex) => {
    let result = false
    keywords.forEach((oneKeyword) => {
      if (result === false) result = regex.test(oneKeyword)
    })

    return result
  }

  return (
    <Autocomplete
      loading={loading}
      options={[
        ...categories?.map((c) => ({ ...c, type: 'category' })),
        ...lawyers.map((l) => ({ ...l, type: 'lawyer' })),
      ]}
      onChange={handleChange}
      filterOptions={(options, state) => {
        const regex = new RegExp(removeDiacritics(state.inputValue), 'i')
        return options.filter((option) => {
          if (option.type === 'category') {
            return (
              regex.test(removeDiacritics(option.name)) || haveKeywordMatch(option.keywords, regex)
            )
          } else {
            const value = `${option.profile.firstname} ${option.profile.lastname}`
            return regex.test(removeDiacritics(value))
          }
        })
      }}
      fullWidth
      popupIcon={null}
      disableListWrap
      value={valueInit}
      getOptionLabel={(option) =>
        option?.type === 'category'
          ? option?.name
          : `${option?.profile?.firstname || ''} ${option?.profile?.lastname || ''}`
      }
      noOptionsText={formatMessage({
        id: 'SearchBar.noOptions',
        defaultMessage: 'Pas de correspondance',
      })}
      groupBy={(option) => option.type}
      PaperComponent={(props) => <Card {...props} style={{ border: '1px solid #D5DEE6' }} />}
      renderGroup={({ key, children }) => {
        return (
          <div key={key}>
            {key > 0 && <div style={{ backgroundColor: `#D5DEE6`, height: 1, width: '100%' }} />}
            {children}
          </div>
        )
      }}
      renderOption={(option, { inputValue }) => {
        const parent = categories.find((c) => c._id === option.idParent)

        return (
          <>
            {option.type === 'category' && (
              <div className={classes.item}>
                <img
                  src={`/assets/category/${
                    option?.imageUrlWithExtension || parent?.imageUrlWithExtension
                  }`}
                  alt={option.name}
                />

                <div className={classes.itemTextContainer}>
                  <div className={classes.itemName}>
                    <Highlighter
                      highlightClassName={classes.highLightText}
                      searchWords={[inputValue]}
                      autoEscape={true}
                      textToHighlight={option.name}
                    />
                  </div>
                  {parent && <div className={classes.itemParent}>{parent.name}</div>}
                </div>

                <ChipCategory domaineId={option._id} />
              </div>
            )}
            {option.type === 'lawyer' && (
              <div className={classes.item} onClick={(event) => handleClickOnLawyer(event, option)}>
                <img src={'/assets/profil/prez.svg'} />

                <div className={classes.itemTextContainer}>
                  <div className={classes.itemName}>
                    <Highlighter
                      highlightClassName={classes.highLightText}
                      searchWords={[inputValue]}
                      autoEscape={true}
                      textToHighlight={`${option.profile.firstname} ${option.profile.lastname}`}
                    />
                  </div>

                  <LawyerDomain lawyerToDisplay={option.profile} />
                </div>

                <ChipCategoryLawyer isProOnly={!option.profile.part && option.profile.pro} />
              </div>
            )}
          </>
        )
      }}
      renderInput={(params) => {
        return (
          <div className={classes.container}>
            {!navbar && !isMobile ? (
              <Search className={classes.icon} />
            ) : (
              <div style={{ marginRight: 8 }} />
            )}

            <TextField
              {...params}
              onChange={handlekeyPress}
              placeholder={formatMessage({
                id: 'SearchBar.placeholder',
                defaultMessage: 'Domaine de droit, spécialité, avocat…',
              })}
              classes={classesTextField}
            />
          </div>
        )
      }}
    />
  )
}

const useStyle = makeStyles((theme) => {
  return {
    container: {
      display: 'flex',
      alignItems: 'center',
      width: '100%',
    },
    highLightText: {
      color: theme.palette.secondary.main,
      backgroundColor: 'transparent',
      textDecoration: 'underline',
    },
    icon: {
      color: `#222120`,
      marginRight: 16,
      marginLeft: 16,
      width: '28px !important',
      height: '28px !important',
    },
    item: {
      color: '#44546A',
      fontSize: 14,
      display: 'flex',
      alignItems: 'center',
      width: '100%',
      paddingTop: 6,
      paddingBottom: 6,

      '& img': {
        width: 21,
        marginLeft: 8,
        marginRight: 16,
      },
    },
    itemName: {
      color: '#222120',
      fontSize: 18,
      fontWeight: 900,
    },
    itemParent: {
      textTransform: 'lowercase',
      '&:first-letter': {
        textTransform: 'uppercase',
      },
    },
    itemTextContainer: {
      width: '100%',
    },
  }
})

const useStylesTextField = makeStyles({
  root: {
    right: '8px !important',
    '& .MuiInput-underline:before': {
      borderBottom: '0px !important',
      border: '0px !important',
    },
    '& .MuiInput-underline:after': {
      border: '0px !important',
    },
    '& div': {
      border: '0px !important',
      marginBottom: '0px !important',
      borderBottom: '0px !important',
    },
  },
})

export default AutocompleteCategorie
